import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

/**
 * Identifies the wallet the user most recently signed in with. Used by the
 * login page to render a "Recent" shortcut. Passkey uses the same record, but
 * the login page badges the existing Passkey tile instead of rendering a
 * duplicate Recent tile.
 *
 * Stored minimal — name and icon are re-derived from the live connector /
 * adapter / manifest list at render time. The Recent tile only renders
 * when that lookup succeeds (i.e. the wallet is currently usable), so a
 * stale `name` field would never be displayed; saving it here would only
 * increase the chance of name/icon drift after a wallet renames itself.
 */
export type LastConnectedWallet = {
  chain: "near" | "evm" | "solana" | "stellar" | "ton" | "tron" | "webauthn"
  /**
   * Stable identifier the matching `signIn` strategy can re-use:
   *   - EVM: wagmi connector `id` (e.g. `io.metamask`, `walletConnect`)
   *   - Solana: wallet-adapter `name` (e.g. `Phantom`)
   *   - NEAR: manifest `id` from `@hot-labs/near-connect`
   *   - Stellar: `ISupportedWallet.id` from stellar-wallets-kit
   *   - TON, Tron, WebAuthn: the chain identifier itself (no per-wallet selection)
   */
  walletId: string
}

type State = {
  lastConnectedWallet: LastConnectedWallet | null
}

type Actions = {
  recordLastConnectedWallet: (wallet: LastConnectedWallet) => void
  clearLastConnectedWallet: () => void
}

type Store = State & Actions

export const useLastConnectedWalletStore = create<Store>()(
  persist(
    (set) => ({
      lastConnectedWallet: null,
      recordLastConnectedWallet: (wallet) => {
        set({ lastConnectedWallet: wallet })
      },
      clearLastConnectedWallet: () => {
        set({ lastConnectedWallet: null })
      },
    }),
    {
      name: "app_last_connected_wallet",
      storage: createJSONStorage(() => localStorage),
    }
  )
)
